/**
 * UsageTrendChart Component
 *
 * Displays a line/bar chart showing daily token usage over time.
 * Uses simple CSS-based bars for visualization (no external chart library required).
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-606 (Frontend: UsageTrendChart component)
 * - AC-612 (Parent: UsageDashboard container)
 */

import React from 'react';
import { TrendingUp, AlertCircle } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import type { TokenUsageTrend, TokenUsageTrendDataPoint } from '../../types/tokenUsage';

export interface UsageTrendChartProps {
  /** Token usage trend data from API */
  data: TokenUsageTrend | null;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Format a number with thousands separator using locale formatting.
 */
const formatNumber = (n: number): string => {
  return n.toLocaleString('en-US');
};

/**
 * Format a date string to a shorter display format.
 */
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Format a number as compact currency (e.g., $1.2K).
 */
const formatCompactCurrency = (n: number): string => {
  if (n >= 1000) {
    return `$${(n / 1000).toFixed(1)}K`;
  }
  return `$${n.toFixed(2)}`;
};

/**
 * Loading skeleton for the chart.
 */
const LoadingSkeleton: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading trend data"
    data-testid="trend-chart-loading"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse"
  >
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4" />
    <div className="flex items-end space-x-2 h-48">
      {[...Array(7)].map((_, i) => (
        <div
          key={i}
          className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-t"
          style={{ height: `${Math.random() * 60 + 20}%` }}
        />
      ))}
    </div>
    <div className="flex justify-between mt-4">
      {[...Array(7)].map((_, i) => (
        <div key={i} className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10" />
      ))}
    </div>
  </div>
);

/**
 * Error state component.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    data-testid="trend-chart-error"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
  >
    <div className="flex items-center text-red-600 mb-4">
      <AlertCircle data-testid="trend-error-icon" className="w-5 h-5 mr-2" />
      <span className="font-medium">Error loading trend data</span>
    </div>
    <p className="text-gray-600 dark:text-gray-400">{message}</p>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC = () => (
  <div
    data-testid="trend-chart-empty"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 text-center"
  >
    <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
    <p className="text-gray-500 dark:text-gray-400">No trend data available</p>
    <p className="text-sm text-gray-400 mt-1">Usage data will appear here once you start using AI features</p>
  </div>
);

/**
 * Calculate the maximum value for scaling the chart.
 */
const getMaxValue = (dataPoints: TokenUsageTrendDataPoint[]): number => {
  if (dataPoints.length === 0) return 1;
  const max = Math.max(...dataPoints.map(d => d.total_tokens));
  return max === 0 ? 1 : max;
};

/**
 * Get bar color based on the dominant source type for that day.
 */
const getBarColors = (dataPoint: TokenUsageTrendDataPoint): { orchestration: string; assistant: string; embedding: string } => {
  return {
    orchestration: 'bg-blue-500',
    assistant: 'bg-purple-500',
    embedding: 'bg-green-500',
  };
};

/**
 * UsageTrendChart component displays daily token usage over time.
 */
const UsageTrendChart: React.FC<UsageTrendChartProps> = ({
  data,
  loading = false,
  error = null,
}) => {
  // Show loading state only if we don't have existing data to display
  if (loading && (!data || data.data_points.length === 0)) {
    return <LoadingSkeleton />;
  }

  // Show error state only if no data to fall back on
  if (error && (!data || data.data_points.length === 0)) {
    return <ErrorState message={error} />;
  }

  // Show empty state when no data and not loading
  if (!data || data.data_points.length === 0) {
    return <EmptyState />;
  }

  const maxValue = getMaxValue(data.data_points);
  const chartHeight = 200; // pixels

  return (
    <div
      data-testid="usage-trend-chart"
      className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <TrendingUp data-testid="trend-chart-icon" className="w-5 h-5 text-primary-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Usage Trend</h3>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(data.period_start)} - {formatDate(data.period_end)}
        </div>
      </div>

      {/* Legend */}
      <div
        data-testid="trend-chart-legend"
        className="flex items-center space-x-4 mb-4 text-sm"
      >
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded mr-1" />
          <span className="text-gray-600 dark:text-gray-400">Workflow</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-purple-500 rounded mr-1" />
          <span className="text-gray-600 dark:text-gray-400">Assistant</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded mr-1" />
          <span className="text-gray-600 dark:text-gray-400">Embedding</span>
        </div>
      </div>

      {/* Chart Area */}
      <div
        data-testid="trend-chart-bars"
        className="relative"
        style={{ height: `${chartHeight}px` }}
      >
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-16 flex flex-col justify-between text-xs text-gray-400 pr-2">
          <span>{formatNumber(maxValue)}</span>
          <span>{formatNumber(Math.round(maxValue * 0.5))}</span>
          <span>0</span>
        </div>

        {/* Bars container */}
        <div className={`ml-16 h-full flex items-end ${data.data_points.length > 1 ? 'space-x-1' : 'justify-center'}`}>
          {data.data_points.map((dataPoint, index) => {
            const workflowHeight = maxValue > 0
              ? (dataPoint.orchestration_tokens / maxValue) * 100
              : 0;
            const assistantHeight = maxValue > 0
              ? (dataPoint.assistant_tokens / maxValue) * 100
              : 0;
            const embeddingHeight = maxValue > 0
              ? (dataPoint.embedding_tokens / maxValue) * 100
              : 0;
            const hasSourceBreakdown = workflowHeight > 0 || assistantHeight > 0 || embeddingHeight > 0;
            const totalHeight = maxValue > 0
              ? (dataPoint.total_tokens / maxValue) * 100
              : 0;

            return (
              <div
                key={dataPoint.date}
                data-testid={`trend-bar-${index}`}
                className={`flex flex-col justify-end h-full group relative ${data.data_points.length === 1 ? 'w-16' : 'flex-1'}`}
                title={`${formatDate(dataPoint.date)}: ${formatNumber(dataPoint.total_tokens)} tokens (${formatCompactCurrency(dataPoint.total_cost)})`}
              >
                {/* Stacked bars */}
                <div className="flex flex-col justify-end">
                  {hasSourceBreakdown ? (
                    <>
                      {workflowHeight > 0 && (
                        <div
                          className="bg-blue-500 rounded-t transition-all duration-200 group-hover:bg-primary-600"
                          style={{ height: `${(workflowHeight / 100) * chartHeight}px` }}
                        />
                      )}
                      {assistantHeight > 0 && (
                        <div
                          className="bg-purple-500 transition-all duration-200 group-hover:bg-purple-600"
                          style={{ height: `${(assistantHeight / 100) * chartHeight}px` }}
                        />
                      )}
                      {embeddingHeight > 0 && (
                        <div
                          className="bg-green-500 transition-all duration-200 group-hover:bg-green-600"
                          style={{ height: `${(embeddingHeight / 100) * chartHeight}px` }}
                        />
                      )}
                    </>
                  ) : totalHeight > 0 && (
                    <div
                      className="bg-blue-500 rounded-t transition-all duration-200 group-hover:bg-primary-600"
                      style={{ height: `${(totalHeight / 100) * chartHeight}px` }}
                    />
                  )}
                </div>

                {/* Hover tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                  <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    <div className="font-medium">{formatDate(dataPoint.date)}</div>
                    <div>{formatNumber(dataPoint.total_tokens)} tokens</div>
                    <div>{formatCompactCurrency(dataPoint.total_cost)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels */}
      <div
        data-testid="trend-chart-x-labels"
        className="ml-16 flex justify-between mt-2 text-xs text-gray-400"
      >
        {data.data_points.map((dataPoint, index) => {
          // Show label for every nth bar depending on total count
          const showLabel = data.data_points.length <= 10 || index % Math.ceil(data.data_points.length / 7) === 0;
          return (
            <span key={dataPoint.date} className="flex-1 text-center">
              {showLabel ? formatDate(dataPoint.date) : ''}
            </span>
          );
        })}
      </div>

      {/* Summary */}
      <div
        data-testid="trend-chart-summary"
        className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-4 text-sm"
      >
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Tokens:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
            {formatNumber(data.data_points.reduce((sum, d) => sum + d.total_tokens, 0))}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Cost:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
            {formatCompactCurrency(data.data_points.reduce((sum, d) => sum + d.total_cost, 0))}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Days:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">{data.data_points.length}</span>
        </div>
      </div>
    </div>
  );
};

export default UsageTrendChart;
